import React from 'react';
import LegalLayout, { LegalSection } from '../components/LegalLayout';

export default function PolitiqueConfidentialite() {
  return (
    <LegalLayout title="Politique de confidentialité" lastUpdated="1er janvier 2025">
      <p className="text-[#a0a0ab]">
        Kurso attache une grande importance à la protection de vos données personnelles. Cette politique explique comment nous collectons, utilisons et protégeons vos données conformément au RGPD (UE 2016/679) et à la loi Informatique et Libertés.
      </p>

      <LegalSection title="1. Responsable du traitement">
        <p>
          <strong>Kurso SAS</strong><br />
          Email : <a href="mailto:dpo@mykurso.fr" className="text-[#ff6b6b] hover:underline">dpo@mykurso.fr</a><br />
          Délégué à la Protection des Données (DPO) : dpo@mykurso.fr
        </p>
      </LegalSection>

      <LegalSection title="2. Données collectées">
        <p>Nous collectons les données suivantes selon votre utilisation :</p>
        <div className="space-y-3 mt-2">
          <div className="bg-white/5 rounded-xl p-3">
            <p className="font-medium text-white text-xs mb-1">Données de compte</p>
            <ul className="list-disc pl-4 space-y-1 text-[#a0a0ab] text-xs">
              <li>Adresse email (identifiant unique)</li>
              <li>Prénom (affiché dans l'interface)</li>
              <li>Mot de passe haché (non lisible)</li>
              <li>Date de création du compte</li>
            </ul>
          </div>
          <div className="bg-white/5 rounded-xl p-3">
            <p className="font-medium text-white text-xs mb-1">Données d'apprentissage</p>
            <ul className="list-disc pl-4 space-y-1 text-[#a0a0ab] text-xs">
              <li>Cours générés et progression</li>
              <li>Scores aux quiz</li>
              <li>Points XP et badges obtenus</li>
              <li>Série quotidienne</li>
              <li>Résultats du Bilan Kurso</li>
            </ul>
          </div>
          <div className="bg-white/5 rounded-xl p-3">
            <p className="font-medium text-white text-xs mb-1">Données de paiement</p>
            <ul className="list-disc pl-4 space-y-1 text-[#a0a0ab] text-xs">
              <li>Historique des transactions (via Stripe)</li>
              <li>Plan d'abonnement actif</li>
              <li><em>Les données bancaires sont traitées exclusivement par Stripe — nous n'y accédons pas.</em></li>
            </ul>
          </div>
          <div className="bg-white/5 rounded-xl p-3">
            <p className="font-medium text-white text-xs mb-1">Données techniques (avec consentement analytique)</p>
            <ul className="list-disc pl-4 space-y-1 text-[#a0a0ab] text-xs">
              <li>Pages visitées, fonctionnalités utilisées</li>
              <li>Type d'appareil et navigateur</li>
              <li>Données d'utilisation anonymisées</li>
            </ul>
          </div>
        </div>
      </LegalSection>

      <LegalSection title="3. Finalités et bases légales">
        <div className="space-y-2">
          <div className="flex gap-3 text-xs">
            <div className="w-2 h-2 rounded-full bg-[#ff6b6b] mt-1.5 flex-shrink-0" />
            <div>
              <p className="text-white font-medium">Fourniture du service</p>
              <p className="text-[#a0a0ab]">Base : Exécution du contrat. Gestion de votre compte, génération de cours, suivi de progression.</p>
            </div>
          </div>
          <div className="flex gap-3 text-xs">
            <div className="w-2 h-2 rounded-full bg-[#ffd93d] mt-1.5 flex-shrink-0" />
            <div>
              <p className="text-white font-medium">Paiements</p>
              <p className="text-[#a0a0ab]">Base : Exécution du contrat. Traitement des abonnements via Stripe.</p>
            </div>
          </div>
          <div className="flex gap-3 text-xs">
            <div className="w-2 h-2 rounded-full bg-[#06d6a0] mt-1.5 flex-shrink-0" />
            <div>
              <p className="text-white font-medium">Analyses d'utilisation</p>
              <p className="text-[#a0a0ab]">Base : Consentement. Mesure d'audience via Mixpanel pour améliorer la plateforme.</p>
            </div>
          </div>
          <div className="flex gap-3 text-xs">
            <div className="w-2 h-2 rounded-full bg-white/40 mt-1.5 flex-shrink-0" />
            <div>
              <p className="text-white font-medium">Communications transactionnelles</p>
              <p className="text-[#a0a0ab]">Base : Intérêt légitime. Confirmation d'inscription, notifications de série quotidienne (via Brevo).</p>
            </div>
          </div>
          <div className="flex gap-3 text-xs">
            <div className="w-2 h-2 rounded-full bg-white/40 mt-1.5 flex-shrink-0" />
            <div>
              <p className="text-white font-medium">Obligations légales</p>
              <p className="text-[#a0a0ab]">Base : Obligation légale. Conservation des données de facturation (10 ans).</p>
            </div>
          </div>
        </div>
      </LegalSection>

      <LegalSection title="4. Durée de conservation">
        <ul className="list-disc pl-5 space-y-1 text-[#a0a0ab]">
          <li><strong className="text-[#e8e8e8]">Données de compte :</strong> Pendant toute la durée de l'abonnement + 3 ans après clôture</li>
          <li><strong className="text-[#e8e8e8]">Données d'apprentissage :</strong> Pendant l'existence du compte</li>
          <li><strong className="text-[#e8e8e8]">Données de facturation :</strong> 10 ans (obligation légale)</li>
          <li><strong className="text-[#e8e8e8]">Données analytiques :</strong> 13 mois maximum</li>
          <li><strong className="text-[#e8e8e8]">Tentatives de connexion :</strong> 30 jours</li>
        </ul>
      </LegalSection>

      <LegalSection title="5. Destinataires des données">
        <p>Vos données peuvent être partagées avec les sous-traitants suivants :</p>
        <ul className="list-disc pl-5 space-y-1 text-[#a0a0ab] mt-2">
          <li><strong className="text-[#e8e8e8]">Stripe</strong> — Traitement des paiements (États-Unis, clauses contractuelles standard)</li>
          <li><strong className="text-[#e8e8e8]">Anthropic</strong> — Génération de contenus IA (États-Unis, clauses contractuelles standard)</li>
          <li><strong className="text-[#e8e8e8]">Brevo</strong> — Envoi d'emails transactionnels (France/UE)</li>
          <li><strong className="text-[#e8e8e8]">Mixpanel</strong> — Analytiques (États-Unis, avec consentement, clauses contractuelles standard)</li>
        </ul>
        <p className="mt-2">Nous ne vendons jamais vos données personnelles à des tiers.</p>
      </LegalSection>

      <LegalSection title="6. Transferts hors UE">
        <p>
          Certains sous-traitants (Stripe, Anthropic, Mixpanel) sont établis aux États-Unis. Ces transferts sont encadrés par les Clauses Contractuelles Types (CCT) de la Commission Européenne, garantissant un niveau de protection adéquat.
        </p>
      </LegalSection>

      <LegalSection title="7. Vos droits">
        <p>Conformément au RGPD, vous disposez des droits suivants :</p>
        <ul className="list-disc pl-5 space-y-1 text-[#a0a0ab] mt-2">
          <li><strong className="text-[#e8e8e8]">Droit d'accès :</strong> Obtenir une copie de vos données</li>
          <li><strong className="text-[#e8e8e8]">Droit de rectification :</strong> Corriger des données inexactes</li>
          <li><strong className="text-[#e8e8e8]">Droit à l'effacement :</strong> Supprimer votre compte et données</li>
          <li><strong className="text-[#e8e8e8]">Droit à la portabilité :</strong> Récupérer vos données en format JSON</li>
          <li><strong className="text-[#e8e8e8]">Droit d'opposition :</strong> S'opposer à certains traitements</li>
          <li><strong className="text-[#e8e8e8]">Droit à la limitation :</strong> Restreindre temporairement un traitement</li>
        </ul>
        <p className="mt-2">
          Pour exercer vos droits : <a href="/donnees-personnelles" className="text-[#ff6b6b] hover:underline">voir notre page dédiée</a> ou contactez-nous à{' '}
          <a href="mailto:dpo@mykurso.fr" className="text-[#ff6b6b] hover:underline">dpo@mykurso.fr</a>.
        </p>
        <p className="mt-2">
          Vous pouvez également introduire une réclamation auprès de la <strong>CNIL</strong> (www.cnil.fr) si vous estimez que le traitement de vos données n'est pas conforme.
        </p>
      </LegalSection>

      <LegalSection title="8. Sécurité">
        <p>
          Kurso met en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données : chiffrement des mots de passe (bcrypt), connexions HTTPS, authentification par jetons JWT httpOnly, protection contre les attaques par force brute.
        </p>
      </LegalSection>

      <LegalSection title="9. Mineurs">
        <p>
          Kurso est destiné aux personnes âgées de 13 ans et plus. Les utilisateurs âgés de 13 à 16 ans doivent disposer du consentement parental. Nous ne collectons pas sciemment de données sur des enfants de moins de 13 ans.
        </p>
      </LegalSection>

      <LegalSection title="10. Contact">
        <p>
          Pour toute question relative à cette politique :{' '}
          <a href="mailto:dpo@mykurso.fr" className="text-[#ff6b6b] hover:underline">dpo@mykurso.fr</a>
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
